import { Schema } from "effect";
import { ConversationId, DocumentId, ViewerId } from "./ids.ts";

// The light conversation record (ADR-0064): the studio's only durable trace of an agent conversation
// now that the `session_event` transcript mirror is retired. One row per **Conversation** — a
// client-minted `ConversationId` bound to exactly one managed-agent session (`sessionId`). The
// managed platform is the system of record for the transcript itself (its console + `listEvents`);
// this record only carries what the chat panel needs to *list* a viewer's own conversations and
// *resolve* the resume handle for the next turn. It never holds message content.
//
// `owner` is required — every conversation is created by a request that carries a `CurrentViewer`
// (ADR-0013), so there is no unowned/legacy bucket here (a fresh, un-backfilled table). It stays
// server-side: the wire projection (`ConversationSummary`) exposes only `isMine`/`ownerName`, never
// the raw id, exactly as the retired session-log projection did. `title` is absent until the one-shot
// LLM titling lands (ADR-0064); it is then persisted here *and* pushed to the managed session's
// `title` so the studio list and the Anthropic console show the same string. `running` is set while a
// turn is in flight and cleared when it settles — it drives the panel's "reattach to a live turn"
// affordance without polling the managed API on every list.
export const ConversationRecord = Schema.Struct({
  conversationId: ConversationId,
  // The managed-agent session id (Managed Agents `/v1/sessions/{id}`): the resume handle and the
  // console deep-link. A plain string — the vendor mints it, so the studio never brands it.
  sessionId: Schema.String,
  documentId: DocumentId,
  owner: ViewerId,
  // The owner's stamped IdP display name (ADR-0050); absent for anonymous-era viewers, where the read
  // falls back to `deriveDisplayName(owner)`.
  ownerDisplayName: Schema.optional(Schema.String),
  // The LLM-generated title; absent until the one-shot titling completes after the first turn.
  title: Schema.optional(Schema.String),
  turnCount: Schema.Int.pipe(Schema.nonNegative()),
  running: Schema.Boolean,
  createdAt: Schema.DateTimeUtc,
  lastTurnAt: Schema.DateTimeUtc,
});
export type ConversationRecord = typeof ConversationRecord.Type;

// One list-row summary of a **Conversation** for the chat panel's thread list (ADR-0064, superseding
// the session-log-derived summary of ADR-0041/0042). Projected from a `ConversationRecord`:
// `title` is the LLM title (empty string until it lands — the panel treats empty as "New
// conversation"); `startedAt`/`lastEventAt` are the record's `createdAt`/`lastTurnAt`; `isMine` is
// `owner === requester` (always true in a `scope=mine` list, kept on the wire for the renderer);
// `ownerName` is the derived/stamped display name; `isRunning` drives the live-turn reattach
// affordance. No raw `ViewerId` and no managed `sessionId` ever cross this wire (the server resolves
// the session id from the record; the browser addresses a conversation by `conversationId` only).
export const ConversationSummary = Schema.Struct({
  conversationId: ConversationId,
  documentId: DocumentId,
  title: Schema.String,
  startedAt: Schema.DateTimeUtc,
  lastEventAt: Schema.DateTimeUtc,
  turnCount: Schema.Int.pipe(Schema.nonNegative()),
  isMine: Schema.Boolean,
  ownerName: Schema.optional(Schema.String),
  isRunning: Schema.Boolean,
});
export type ConversationSummary = typeof ConversationSummary.Type;

// A human-supplied conversation title, decoded only at the HTTP rename boundary (`RenameConversationRequest`,
// wired to a route in a later unit). `Schema.Trim` *transforms* the input — leading/trailing whitespace is
// stripped before the length checks run — so a whitespace-only title (`"   "`) decodes to `""` and is then
// rejected by `minLength(1)`, turning it into a 400 at the boundary rather than a stored blank. This is
// deliberately stricter than `ConversationStore.setTitle`'s plain-`string` parameter: the auto-titler is an
// internal LLM producer, not a system boundary, and must remain free to write an over-long title by
// truncating it rather than have the store reject it (see `CLAUDE.md`'s "validate only at system
// boundaries"). The 120-char cap keeps a title readable in the panel's narrow thread-list rail.
// Exported so the client-side input affordance (`chat/conversation-header.tsx`'s `maxLength`) mirrors
// this cap by reference rather than a hand-copied literal.
export const CONVERSATION_TITLE_MAX_LENGTH = 120;

export const ConversationTitle = Schema.Trim.pipe(
  Schema.minLength(1),
  Schema.maxLength(CONVERSATION_TITLE_MAX_LENGTH),
  Schema.brand("ConversationTitle"),
);
export type ConversationTitle = typeof ConversationTitle.Type;

// The HTTP rename request body (wired to a route in a later unit): the one place a raw, untrusted
// title string is parsed into a `ConversationTitle`.
export const RenameConversationRequest = Schema.Struct({
  title: ConversationTitle,
});
export type RenameConversationRequest = typeof RenameConversationRequest.Type;
